import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import CompanySwitcher from "@/components/layout/CompanySwitcher";
import {
  ChartLineUp, Receipt, Users, Package, Bank, FileText,
  BookOpen, ShieldCheck, SignOut, Storefront, ListChecks,
  ShoppingCart, Wallet, ArrowsCounterClockwise, TreeStructure,
  UsersThree, CreditCard, Gear, Repeat, Buildings, Airplane, Factory, Question
} from "@phosphor-icons/react";

const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [
      { to: "/app", label: "Dashboard", icon: ChartLineUp, end: true, tid: "nav-dashboard" },
      { to: "/app/agency", label: "Agency Portfolio", icon: Buildings, tid: "nav-agency" },
    ],
  },
  {
    label: "Sales",
    items: [
      { to: "/app/invoices", label: "Invoices", icon: Receipt, tid: "nav-invoices" },
      { to: "/app/recurring", label: "Recurring", icon: Repeat, tid: "nav-recurring" },
      { to: "/app/credit-notes", label: "Credit Notes", icon: ArrowsCounterClockwise, tid: "nav-cn" },
      { to: "/app/customers", label: "Customers", icon: Users, tid: "nav-customers" },
    ],
  },
  {
    label: "Purchases",
    items: [
      { to: "/app/purchases", label: "Vendor Bills", icon: ShoppingCart, tid: "nav-purchases" },
      { to: "/app/expenses", label: "Expenses (Quick)", icon: Wallet, tid: "nav-expenses" },
      { to: "/app/tne", label: "Travel & Expense", icon: Airplane, tid: "nav-tne" },
      { to: "/app/vendors", label: "Vendors", icon: Storefront, tid: "nav-vendors" },
    ],
  },
  {
    label: "ERP",
    items: [
      { to: "/app/erp", label: "Inventory · SO · CRM", icon: Package, tid: "nav-erp" },
      { to: "/app/manufacturing", label: "Manufacturing", icon: Factory, tid: "nav-mfg" },
      { to: "/app/payroll", label: "Payroll & HR", icon: UsersThree, tid: "nav-payroll" },
    ],
  },
  {
    label: "Catalog",
    items: [
      { to: "/app/products", label: "Products & Services", icon: Package, tid: "nav-products" },
    ],
  },
  {
    label: "Compliance",
    items: [
      { to: "/app/gst-returns", label: "GST Returns", icon: FileText, tid: "nav-gst" },
      { to: "/app/gsp-config", label: "e-Invoice GSP", icon: ShieldCheck, tid: "nav-gsp" },
      { to: "/app/bank-recon", label: "Bank Recon", icon: Bank, tid: "nav-bank" },
    ],
  },
  {
    label: "Accounting",
    items: [
      { to: "/app/vouchers", label: "Vouchers", icon: BookOpen, tid: "nav-vouchers" },
      { to: "/app/chart-of-accounts", label: "Chart of Accounts", icon: TreeStructure, tid: "nav-coa" },
      { to: "/app/reports", label: "Reports", icon: ChartLineUp, tid: "nav-reports" },
    ],
  },
  {
    label: "Admin",
    items: [
      { to: "/app/team", label: "Team & Roles", icon: UsersThree, tid: "nav-team" },
      { to: "/app/approval-rules", label: "Approval Chains", icon: ShieldCheck, tid: "nav-approval" },
      { to: "/app/subscription", label: "Subscription", icon: CreditCard, tid: "nav-sub" },
      { to: "/app/settings", label: "Settings", icon: Gear, tid: "nav-settings" },
      { to: "/app/help", label: "Help Center", icon: Question, tid: "nav-help" },
      { to: "/app/audit-logs", label: "Audit Logs", icon: ListChecks, tid: "nav-audit" },
    ],
  },
];

export default function AppShell() {
  const { user, company, logout } = useAuth();
  const nav = useNavigate();

  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#F4F4F5]">
      {/* Sidebar */}
      <aside className="w-60 lb-sidebar flex flex-col shrink-0 h-screen sticky top-0">
        <div className="px-5 py-5 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded bg-[#0A66C2] text-white grid place-items-center font-bold text-sm">L</div>
            <div className="font-display font-extrabold text-lg tracking-tight">LedgerBook</div>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Cloud · v1.2</div>
        </div>

        <div data-testid="active-company">
          {company ? (
            <CompanySwitcher />
          ) : (
            <div className="px-5 py-4 border-b border-neutral-200">
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">Active Company</div>
              <NavLink to="/app/company-setup" className="text-sm underline mt-1 inline-block" data-testid="setup-company-link">
                Setup company →
              </NavLink>
            </div>
          )}
        </div>

        <nav className="flex-1 py-2 overflow-y-auto">
          {NAV_SECTIONS.map((sec) => (
            <div key={sec.label} className="mb-3">
              <div className="px-5 pt-2 pb-1 text-[9px] uppercase tracking-widest text-neutral-400 font-bold">
                {sec.label}
              </div>
              {sec.items.map(({ to, label, icon: Icon, end, tid }) => (
                <NavLink
                  key={to} to={to} end={end}
                  className={({ isActive }) => "lb-nav-link " + (isActive ? "active" : "")}
                  data-testid={tid}
                >
                  <Icon size={16} weight="regular" />
                  {label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="border-t border-neutral-200 p-4">
          <div className="text-[11px] text-neutral-500 mb-2 truncate" data-testid="current-user">{user?.email}</div>
          <button onClick={onLogout} className="lb-btn lb-btn-ghost lb-btn-sm w-full" data-testid="logout-btn">
            <SignOut size={14} /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="px-8 py-8 max-w-[1400px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
